import React, { useRef, useEffect } from 'react';
import type { ChipDetection } from '@/services/chipDetection';

interface DetectionOverlayProps {
  imageData: ImageData;
  detections: ChipDetection[];
}

const COLOR_PALETTE = ['#FF0000', '#0066FF', '#00CC00', '#FFCC00', '#000000', '#9900CC', '#FF8800', '#FFFF00', '#FF69B4', '#8B4513'];

export function DetectionOverlay({ imageData, detections }: DetectionOverlayProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.width = imageData.width;
    canvas.height = imageData.height;
    const ctx = canvas.getContext('2d')!;

    // Draw original image
    ctx.putImageData(imageData, 0, 0);

    // Group detections by className for color assignment
    const uniqueClasses = [...new Set(detections.map(d => d.className))];

    // Draw bounding ellipses
    for (const det of detections) {
      const colorIdx = uniqueClasses.indexOf(det.className);
      const color = COLOR_PALETTE[colorIdx % COLOR_PALETTE.length];
      const cx = (det.bbox.x + det.bbox.width / 2) * imageData.width;
      const cy = (det.bbox.y + det.bbox.height / 2) * imageData.height;
      const rx = (det.bbox.width / 2) * imageData.width;
      const ry = (det.bbox.height / 2) * imageData.height;

      ctx.beginPath();
      ctx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2);
      ctx.strokeStyle = color;
      ctx.lineWidth = 3;
      ctx.stroke();

      // Label
      ctx.font = 'bold 13px Manrope, sans-serif';
      const label = det.className;
      const textW = ctx.measureText(label).width;
      ctx.fillStyle = color + 'CC';
      ctx.fillRect(cx - textW / 2 - 4, cy - 16, textW + 8, 20);
      ctx.fillStyle = '#FFFFFF';
      ctx.textAlign = 'center';
      ctx.fillText(label, cx, cy - 2);
    }
  }, [imageData, detections]);

  return <canvas ref={canvasRef} className="w-full rounded-lg" />;
}
